import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil } from "lucide-react";
import { partnersApi, tierChip, type Tier, type TierUpsert } from "@/services/b2b";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { formatCents } from "@/services/finance";
import { useCan } from "@/auth/permissions";

/**
 * 客户分级配置 — 动态可配（partner_tiers）· 定价管理子页 /admin/pricing/tiers。
 * 老板/管理员可增删改级别 · 每级绑定 自动定级阈值 + 信用区间 + baseline markup + 账期。
 */
const TERMS = ["prepaid", "net15", "net30", "net45"] as const;
const TERMS_LABEL: Record<string, string> = { prepaid: "预付", net15: "NET 15", net30: "NET 30", net45: "NET 45" };

const BLANK: TierUpsert = {
  code: "", name: "", sort_order: 0, min_monthly_orders: 0, min_annual_gmv_cents: 0,
  credit_min_cents: 0, credit_max_cents: 0, baseline_markup_pct: 0, settlement_terms: "prepaid", is_active: true,
};

export function TierConfigPage() {
  const qc = useQueryClient();
  const isOwner = useCan("channel_partners.tiers.manage");   // 有「分级配置」权限点可编辑·其它只读
  const tiersQ = useQuery({ queryKey: ["b2b", "tiers"], queryFn: () => partnersApi.listTiers() });
  const [editing, setEditing] = useState<Tier | null>(null);
  const [adding, setAdding] = useState(false);

  const del = useMutation({
    mutationFn: (id: string) => partnersApi.deleteTier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["b2b", "tiers"] }),
    onError: (e) => alert((e as Error).message || "删除失败"),
  });

  if (tiersQ.isLoading) return <LoadingState />;
  if (tiersQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>分级配置加载失败</span>
      <button onClick={() => tiersQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  const tiers = (tiersQ.data ?? []).slice().sort((a, b) => b.sort_order - a.sort_order);

  return (
    <PageShell
      title="🏷 客户分级配置"
      subtitle={isOwner ? "动态可配 · 每级绑定 自动定级阈值 + 信用额度区间 + baseline markup + 账期 · 改动进操作日志" : "仅老板 / 超级管理员可编辑 · 当前为只读视图"}
      actions={
        isOwner ? (
          <button type="button" onClick={() => setAdding(true)} className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700">
            <Plus className="h-3.5 w-3.5" /> 添加级别
          </button>
        ) : (
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[12px] font-medium text-slate-500">🔒 只读 · 仅老板 / 超管可改</span>
        )
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">级别</th>
              <th className="px-4 py-3 text-left">自动定级阈值</th>
              <th className="px-4 py-3 text-left">信用额度区间</th>
              <th className="px-4 py-3 text-right">markup</th>
              <th className="px-4 py-3 text-left">账期</th>
              {isOwner && <th className="px-4 py-3 text-right">操作</th>}
            </tr>
          </thead>
          <tbody>
            {tiers.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tierChip(t.code)}`}>{t.name}</span>
                  <span className="ml-2 text-[11px] text-slate-400">{t.code}</span>
                  {!t.is_active && <span className="ml-2 text-[11px] text-rose-500">停用</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">月单 ≥ {t.min_monthly_orders} 或 年流水 ≥ {formatCents(t.min_annual_gmv_cents)}</td>
                <td className="px-4 py-3 text-slate-600">{formatCents(t.credit_min_cents)} – {formatCents(t.credit_max_cents)}</td>
                <td className="px-4 py-3 text-right font-medium">{t.baseline_markup_pct}%</td>
                <td className="px-4 py-3 text-slate-600">{TERMS_LABEL[t.settlement_terms] ?? t.settlement_terms}</td>
                {isOwner && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setEditing(t)} className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /></button>
                      <button type="button" disabled={del.isPending} onClick={() => { if (confirm(`删除分级「${t.name}」？`)) del.mutate(t.id); }} className="rounded-md border border-rose-300 p-1.5 text-rose-600 hover:bg-rose-50 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {tiers.length === 0 && <tr><td colSpan={isOwner ? 6 : 5} className="px-4 py-6 text-center text-slate-500">暂无分级{isOwner ? " · 点「添加级别」新建" : ""}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-400">💡 系统按业务量自动把客户定到「满足阈值的最高级」· 单客户可在详情手动 override 锁定。</div>

      {(adding || editing) && (
        <TierForm
          init={editing ?? BLANK}
          isEdit={!!editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); qc.invalidateQueries({ queryKey: ["b2b", "tiers"] }); }}
          editId={editing?.id}
        />
      )}
    </PageShell>
  );
}

function TierForm({ init, isEdit, onClose, onSaved, editId }: { init: TierUpsert; isEdit: boolean; onClose: () => void; onSaved: () => void; editId?: string }) {
  const [f, setF] = useState<TierUpsert>({ ...init });
  const up = (patch: Partial<TierUpsert>) => setF((x) => ({ ...x, ...patch }));
  const save = useMutation({
    mutationFn: () => isEdit && editId ? partnersApi.updateTier(editId, f) : partnersApi.createTier(f),
    onSuccess: onSaved,
    onError: (e) => alert((e as Error).message || "保存失败"),
  });
  const dollars = (cents: number) => (cents / 100).toString();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 text-[15px] font-bold text-slate-900">{isEdit ? "编辑分级" : "添加分级"}</div>
        <div className="grid grid-cols-2 gap-3">
          <FF label="级别名 *"><input value={f.name} onChange={(e) => up({ name: e.target.value })} className={INP} /></FF>
          <FF label="代码 *（英文·唯一）"><input value={f.code} disabled={isEdit} onChange={(e) => up({ code: e.target.value })} className={`${INP} disabled:bg-slate-100`} /></FF>
          <FF label="级别排序（越大越高）"><input type="number" value={f.sort_order} onChange={(e) => up({ sort_order: Number(e.target.value) })} className={INP} /></FF>
          <FF label="账期"><select value={f.settlement_terms} onChange={(e) => up({ settlement_terms: e.target.value })} className={INP}>{TERMS.map((t) => <option key={t} value={t}>{TERMS_LABEL[t]}</option>)}</select></FF>
          <FF label="自动定级 · 月单 ≥"><input type="number" value={f.min_monthly_orders} onChange={(e) => up({ min_monthly_orders: Number(e.target.value) })} className={INP} /></FF>
          <FF label="自动定级 · 年流水 ≥ ($)"><input type="number" value={dollars(f.min_annual_gmv_cents)} onChange={(e) => up({ min_annual_gmv_cents: Math.round(Number(e.target.value) * 100) || 0 })} className={INP} /></FF>
          <FF label="信用额度下限 ($)"><input type="number" value={dollars(f.credit_min_cents)} onChange={(e) => up({ credit_min_cents: Math.round(Number(e.target.value) * 100) || 0 })} className={INP} /></FF>
          <FF label="信用额度上限 ($)"><input type="number" value={dollars(f.credit_max_cents)} onChange={(e) => up({ credit_max_cents: Math.round(Number(e.target.value) * 100) || 0 })} className={INP} /></FF>
          <FF label="baseline markup (%)"><input type="number" value={f.baseline_markup_pct} onChange={(e) => up({ baseline_markup_pct: Number(e.target.value) })} className={INP} /></FF>
          <FF label="启用"><select value={f.is_active ? "1" : "0"} onChange={(e) => up({ is_active: e.target.value === "1" })} className={INP}><option value="1">启用</option><option value="0">停用</option></select></FF>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-medium text-slate-600">取消</button>
          <button type="button" disabled={save.isPending || !f.name || !f.code} onClick={() => save.mutate()} className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50">{save.isPending ? "保存中…" : "保存"}</button>
        </div>
      </div>
    </div>
  );
}

const INP = "h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px] focus:border-blue-500 focus:outline-none";
function FF({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-slate-600">{label}</span>{children}</label>;
}
